import { Locale } from "next/router";

import {
  type ContentLocale,
  defaultLocale,
  locales,
  toContentLocale,
} from "@/i18n/config";

/**
 * Lifecycle for a product listing after it has sold (WHO-4840, GEO-1).
 *
 * Historically every `gone` product was answered with a 301 to a listing
 * page. That throws away the URL: Google drops it from the index, and an
 * assistant that cited the page has nothing left to resolve. Instead we
 * age the listing out in three steps, so the URL keeps answering for as
 * long as it is still useful and only collapses into a redirect once the
 * content is genuinely stale.
 *
 * Phase boundaries are expressed in days since the sale.
 */
export const SOFT_SOLD_MAX_AGE_DAYS = 90;
export const RECENTLY_GONE_MAX_AGE_DAYS = 365;

export const SOLD_PHASE = {
  /** 0 to 90 days. HTTP 200, indexable, similar items on the page. */
  SoftSold: "soft-sold",
  /**
   * 90 to 365 days. The same page, also at HTTP 200 and also indexable.
   *
   * This phase used to answer 410. See `resolveProductAvailability` for
   * why it no longer does: the sold URL is the entry point, and a 410 is
   * the fastest way to destroy it.
   */
  RecentlyGone: "recently-gone",
  /** 365 days or older, or no usable sale date. HTTP 301 to a listing page. */
  LongGone: "long-gone",
} as const;

export type SoldPhase = (typeof SOLD_PHASE)[keyof typeof SOLD_PHASE];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Last resort listing page per locale, resolved from the gateway's
 * `listingPage(input: {url: "furniture"})` translations. Used only when
 * neither `relatedListingPages` nor `getFallbackListingPage` yields a
 * path, which is the case the old code let degrade into a redirect to
 * `/` or `/<locale>/undefined`.
 */
export const DEFAULT_FALLBACK_LISTING_PATH: Record<ContentLocale, string> = {
  en: "furniture",
  nl: "meubels",
  de: "mobel",
  fr: "meubles",
  es: "muebles",
  it: "mobili",
};

const LAST_RESORT_LISTING_PATH = "furniture";

type SoldAuctionLike = {
  state?: string | null;
  end_date?: string | null;
  start_date?: string | null;
  updated?: string | null;
  bid_count?: number | null;
} | null;

export type SoldProductLike = {
  updated?: string | null;
  gone?: boolean | null;
  /** Product (curation) state. CANCELED = withdrawn by the seller. */
  state?: string | null;
  auction?: SoldAuctionLike;
} | null;

const toDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Auction states that mean the item is sold.
 *
 * DOMAIN RULE (Thomas, 2026-08-01), and the single place it lives:
 * availability is determined solely by the auction being `PUBLISHED`.
 * Once an item is sold it is no longer available, even if the order is
 * not yet Completed. **Availability must never be inferred from order
 * state.**
 *
 *  - `RESERVED`  sold, order still in flight. The transitions confirm it:
 *                RESERVED goes back to PUBLISHED when a sale falls
 *                through, and [RESERVED, PUBLISHED, CANCELED] go to
 *                COMPLETED. Sold either way.
 *  - `COMPLETED` sold and closed.
 *
 * Everything else is NOT sold, and is deliberately excluded:
 *  - `PUBLISHED`, `COMING_SOON`, `BID_ONLY`, `DRAFT` are pre-sale.
 *  - `EXPIRED`, `CANCELED`, `BANNED` are withdrawn. Withdrawn is not
 *    sold, and treating it as sold would put a "Sold on <date>" badge on
 *    a public page for an item nobody ever bought.
 *
 * The backend agrees on the availability half: the search indexer writes
 * `"availability": product.auction.state == AuctionState.PUBLISHED`
 * (`catalog/services/search.py`), with no reference to any order.
 */
const SOLD_AUCTION_STATES = ["RESERVED", "COMPLETED"];

export const isSold = (auction?: SoldAuctionLike) => {
  const state = auction?.state?.toUpperCase();

  return !!state && SOLD_AUCTION_STATES.includes(state);
};

/**
 * Auction states in which the listing is still a normal, live product
 * page.
 *
 * WHO-5240 phrases the trigger as `state !== PUBLISHED`, but taken
 * literally that also captures `COMING_SOON` and `BID_ONLY`, which are
 * pre-sale states for listings that are live and biddable right now.
 * Turning those into "no longer available" pages would be a regression
 * on buyable inventory, so they are listed here explicitly.
 *
 * Anything this function does not recognise is treated as available.
 * This file drives all ~168k product pages: an unknown state must fall
 * back to the normal PDP, never to an unavailable page.
 */
const AVAILABLE_AUCTION_STATES = ["PUBLISHED", "COMING_SOON", "BID_ONLY"];

/**
 * Withdrawn, but reversible.
 *
 * `EXPIRED` goes back to `PUBLISHED` via `refresh()`, `CANCELED` and
 * `BANNED` can both be restored by support. Because the state can
 * revert, the page must never carry a sticky signal (301) and must
 * never claim a sale: a "SOLD" badge on a banned item is a false
 * statement about a sale that never happened.
 */
const REVERSIBLY_UNAVAILABLE_AUCTION_STATES = ["BANNED", "EXPIRED", "CANCELED"];

export const isAvailable = (auction?: SoldAuctionLike) => {
  const state = auction?.state?.toUpperCase();

  // No auction at all: keep the normal page. Failing open is the safe
  // direction on a file that serves every product URL.
  if (!state) {
    return true;
  }

  if (AVAILABLE_AUCTION_STATES.includes(state)) {
    return true;
  }

  if (
    SOLD_AUCTION_STATES.includes(state) ||
    REVERSIBLY_UNAVAILABLE_AUCTION_STATES.includes(state) ||
    state === "DRAFT"
  ) {
    return false;
  }

  // A state added to the enum after this code shipped. Serve the normal
  // page rather than guessing that it means unavailable.
  return true;
};

/**
 * Sibling of `isSold`: unavailable, but not sold and not permanent.
 *
 * Kept next to `isSold` deliberately, so the "is this item still for
 * sale" rule lives in exactly one file (WHO-5240 acceptance criterion).
 */
export const isReversiblyUnavailable = (auction?: SoldAuctionLike) => {
  const state = auction?.state?.toUpperCase();

  return !!state && REVERSIBLY_UNAVAILABLE_AUCTION_STATES.includes(state);
};

/**
 * Did this auction ever go public?
 *
 * `start_date` is stamped when the auction starts accepting bids, so its
 * absence is the closest thing the graph gives us to "never published".
 * A recorded bid is accepted as corroborating evidence, because bids can
 * only be placed on a public auction.
 *
 * Used only to separate a `BANNED` listing that was live and may have
 * been indexed (200 + noindex, so it can come back) from one that was
 * banned before it ever appeared (404, nothing to preserve).
 */
export const wasEverPublished = (auction?: SoldAuctionLike) =>
  !!toDate(auction?.start_date) || (auction?.bid_count ?? 0) > 0;

/**
 * Never public, so there is nothing to preserve and nothing was ever
 * indexed. `DRAFT` always, `BANNED` only when it never went live.
 */
export const isPrePublication = (auction?: SoldAuctionLike) => {
  const state = auction?.state?.toUpperCase();

  if (!state) {
    return false;
  }

  if (state === "DRAFT") {
    return true;
  }

  return state === "BANNED" && !wasEverPublished(auction);
};

/**
 * Best available timestamp for "when did this sell", or null when the
 * item is not sold at all.
 *
 * There is no sale-time field in the graph. `auction.sold_at` is a
 * `Price` (the amount the item sold for), not a date.
 *
 * For `COMPLETED` the backend stamps `auction.end_date` inside the
 * `complete()` transition, so that column is the moment of sale, and it
 * is the same column the nightly `set_products_gone` task reads.
 *
 * For `RESERVED`, `end_date` is NOT the sale date and must not be used.
 * `reserve()` does not stamp it, but an auction that previously expired,
 * cancelled or was banned keeps the stale date from back then, because
 * those transitions do stamp it and `refresh()` does not clear it. That
 * is not hypothetical: `de-club-lounge-chair-by-hans-kaufeld` is
 * RESERVED today with `end_date` 2024-03-07 and `updated` 2026-07-26.
 * Reading `end_date` there would age it at 876 days and 301 away a
 * listing that sold last week.
 *
 * So RESERVED falls back to the auction row's last write. That is a
 * proxy, not a sale time: `updated` is `auto_now`, so any later save
 * pushes it forward. It can therefore only make the item look *more*
 * recently sold, which biases toward keeping the URL alive (200) rather
 * than redirecting it away. On an SEO surface that is the safe direction
 * to be wrong in.
 */
export const getSoldDate = (product: SoldProductLike) => {
  const auction = product?.auction;

  if (!isSold(auction)) {
    return null;
  }

  if (auction?.state?.toUpperCase() === "COMPLETED") {
    const completedAt = toDate(auction.end_date);

    if (completedAt) {
      return completedAt;
    }
  }

  return toDate(auction?.updated) ?? toDate(product?.updated);
};

/** Whole days between the sale and `now`, or null when we have no date. */
export const getDaysSinceSold = (
  soldDate: Date | null,
  now: Date = new Date()
) => {
  if (!soldDate) {
    return null;
  }

  const elapsed = now.getTime() - soldDate.getTime();

  if (!Number.isFinite(elapsed)) {
    return null;
  }

  // A sale date in the future means the clocks disagree, not that the
  // listing is fresh in some negative sense. Clamp it to "just sold".
  return Math.max(0, Math.floor(elapsed / MS_PER_DAY));
};

/**
 * Which phase a sold or gone listing is in.
 *
 * Falls back to `LongGone` in the two cases we must not render a page
 * for, so both keep the redirect behaviour they have today:
 *
 *  - The item is not sold (withdrawn, or never sold). A `gone` product
 *    with an EXPIRED, CANCELED or BANNED auction must never be shown as
 *    "Sold on <date>". The internal boost and listing-fee products are
 *    created with `gone = true` and no real auction, and they land here
 *    too.
 *  - The item is sold but cannot be dated, so we cannot place it in a
 *    phase honestly.
 */
export const resolveSoldPhase = (
  product: SoldProductLike,
  now: Date = new Date()
): SoldPhase => {
  if (!isSold(product?.auction)) {
    return SOLD_PHASE.LongGone;
  }

  const daysSinceSold = getDaysSinceSold(getSoldDate(product), now);

  if (daysSinceSold === null) {
    return SOLD_PHASE.LongGone;
  }

  if (daysSinceSold < SOFT_SOLD_MAX_AGE_DAYS) {
    return SOLD_PHASE.SoftSold;
  }

  if (daysSinceSold < RECENTLY_GONE_MAX_AGE_DAYS) {
    return SOLD_PHASE.RecentlyGone;
  }

  return SOLD_PHASE.LongGone;
};

/**
 * The full availability decision for a product URL (WHO-5240).
 *
 * `resolveSoldPhase` above answers only "how old is this sale", and the
 * page used to reach it through `product.gone`. That gate never fires
 * for a real sale: the nightly `set_products_gone` task sets `gone` only
 * for COMPLETED auctions whose `end_date` is over six months old, so
 * genuinely sold listings never entered the branch at all. This resolver
 * replaces the gate. It keys on `auction.state` and uses `gone` for the
 * one thing it is actually fit for, the six-month archival boundary.
 */
export const AVAILABILITY = {
  /** Still for sale. Normal product page, unchanged. */
  Available: "available",
  /** RESERVED. Sold, but the reservation auto-releases within 48h. */
  SalePending: "sale-pending",
  /** COMPLETED, under 90 days. */
  SoftSold: "soft-sold",
  /** COMPLETED, 90 days up to `gone` or 365 days, whichever is first. */
  RecentlyGone: "recently-gone",
  /** COMPLETED and either `gone` (six months plus) or 365 days old. */
  Archived: "archived",
  /** BANNED / EXPIRED / CANCELED after publication. Never sold. */
  Withdrawn: "withdrawn",
  /** DRAFT, or BANNED before it was ever public. */
  NotFound: "not-found",
} as const;

export type Availability = (typeof AVAILABILITY)[keyof typeof AVAILABILITY];

/** Copy and badge treatment for the unavailable page. */
export const UNAVAILABLE_VARIANT = {
  /** "Sold on <date>". Only ever used where a sale really happened. */
  Sold: "sold",
  /** "Sale pending". Reserved, may return to sale within 48h. */
  Pending: "pending",
  /** "No longer available". Neutral, claims nothing about a sale. */
  Withdrawn: "withdrawn",
} as const;

export type UnavailableVariant =
  (typeof UNAVAILABLE_VARIANT)[keyof typeof UNAVAILABLE_VARIANT];

export type AvailabilityDecision = {
  availability: Availability;
  /** HTTP status the URL should answer with. */
  status: 200 | 301 | 404 | 410;
  /**
   * `robots` content for the page, or null to leave the page's normal
   * indexable default alone. Null is not the same as "index": it means
   * this code does not touch indexation at all.
   */
  robots: string | null;
  /** Which page to render: the real PDP, the unavailable page, neither. */
  render: "pdp" | "unavailable" | "none";
  /** Copy variant, only when `render` is "unavailable". */
  variant: UnavailableVariant | null;
};

const AVAILABLE_DECISION: AvailabilityDecision = {
  availability: AVAILABILITY.Available,
  status: 200,
  robots: null,
  render: "pdp",
  variant: null,
};

/**
 * WHO-6094. Availability for a product that only came back through the
 * `includeWithdrawn` lookup, i.e. the seller deleted (withdrew) it.
 *
 * The auction alone cannot be trusted here: `withdrawProduct` only cancels
 * a live auction, and a product with no auction at all would read as
 * available. So:
 *
 *  - not CANCELED: the product is not withdrawn after all (the curation
 *    changed between the two lookups), so the normal resolver decides.
 *  - never published: 404, exactly like a draft. Nothing to explain.
 *  - otherwise the normal resolver decides, so a withdrawn listing that
 *    had already sold keeps its sold page and the 365-day archive,
 *  - and anything it would still call buyable becomes the neutral
 *    "no longer available" page, `noindex,follow`, never a buyable PDP.
 */
export const resolveWithdrawnProductAvailability = (
  product: SoldProductLike,
  now: Date = new Date()
): AvailabilityDecision => {
  if (product?.state?.toUpperCase() !== "CANCELED") {
    return resolveProductAvailability(product, now);
  }

  if (!wasEverPublished(product?.auction)) {
    return {
      availability: AVAILABILITY.NotFound,
      status: 404,
      robots: null,
      render: "none",
      variant: null,
    };
  }

  const decision = resolveProductAvailability(product, now);

  if (decision.availability !== AVAILABILITY.Available) {
    return decision;
  }

  return {
    availability: AVAILABILITY.Withdrawn,
    status: 200,
    robots: "noindex,follow",
    render: "unavailable",
    variant: UNAVAILABLE_VARIANT.Withdrawn,
  };
};

/**
 * Resolve what a product URL should do, from the auction state.
 *
 * The policy, and why each line is what it is:
 *
 * | State                     | HTTP | Robots         |
 * |---------------------------|------|----------------|
 * | PUBLISHED / COMING_SOON / BID_ONLY, vacation mode | 200 | untouched |
 * | RESERVED                  | 200  | untouched      |
 * | COMPLETED 0-365d          | 200  | index,follow   |
 * | COMPLETED, `gone` or 365d+ | 301 | -              |
 * | BANNED/EXPIRED/CANCELED, once published | 200 | noindex,follow |
 * | BANNED pre-publication, DRAFT | 404 | -           |
 *
 * ## Why a sold listing is indexable
 *
 * Roughly 37,000 sold listings are in Google's index today, and they are
 * the entry point for the whole sold-page strategy: someone searches for
 * a specific piece, lands on the URL that used to sell it, and is offered
 * buyable alternatives from the same subcategory with the same
 * characteristics. That only works while the URL still ranks.
 *
 * The previous policy contradicted the strategy. `noindex` asked Google
 * to drop the URL at the next crawl, and the 410 at 90 days made it
 * permanent, so the pages were built to receive traffic that the routing
 * was busy removing. Since almost every sold listing is older than 90
 * days, the 410 was doing most of the damage.
 *
 * The original argument for `noindex` was that a sold listing should not
 * compete with live inventory in search results. That fear is worth
 * watching, but it is answered by what the page now IS: its content is
 * the alternatives, not the sold item, and a visitor who lands on it gets
 * a route to something buyable rather than a dead end. If it turns out to
 * cannibalise live PDPs or category pages, this is one line to revert.
 *
 * The 365-day archival redirect is unchanged, so a sold URL still ages
 * out; it just gets the full year of being findable first.
 *
 * Withdrawn states stay `noindex`: nothing was sold, and a banned or
 * expired listing has no business ranking.
 *
 * RESERVED keeps its index status because the reservation lasts at most
 * 48 hours and releases automatically. That is shorter than a typical
 * recrawl, so deindexing it risks dropping a page that is about to be
 * buyable again.
 *
 * Withdrawn states get 200 + `noindex` rather than 410 because they are
 * reversible: removing `noindex` restores indexability on the next
 * crawl, whereas a 410 needs a recrawl plus a reindex.
 *
 * Vacation mode needs nothing here. The auction stays PUBLISHED and the
 * existing PDP already disables purchase and explains the pause.
 */
export const resolveProductAvailability = (
  product: SoldProductLike,
  now: Date = new Date()
): AvailabilityDecision => {
  const auction = product?.auction;

  if (isAvailable(auction)) {
    return AVAILABLE_DECISION;
  }

  if (isPrePublication(auction)) {
    return {
      availability: AVAILABILITY.NotFound,
      status: 404,
      robots: null,
      render: "none",
      variant: null,
    };
  }

  // `gone` is the backend's archival boundary: six months after a
  // COMPLETED sale, set by the nightly `set_products_gone` task. It is
  // the only remaining use of the flag, and it is checked before the
  // per-state branches so that any historical row carrying `gone` with
  // some other state keeps the redirect it has today rather than
  // suddenly rendering a page.
  if (product?.gone) {
    return {
      availability: AVAILABILITY.Archived,
      status: 301,
      robots: null,
      render: "none",
      variant: null,
    };
  }

  if (isReversiblyUnavailable(auction)) {
    return {
      availability: AVAILABILITY.Withdrawn,
      status: 200,
      robots: "noindex,follow",
      render: "unavailable",
      variant: UNAVAILABLE_VARIANT.Withdrawn,
    };
  }

  if (auction?.state?.toUpperCase() === "RESERVED") {
    return {
      availability: AVAILABILITY.SalePending,
      status: 200,
      robots: null,
      render: "unavailable",
      variant: UNAVAILABLE_VARIANT.Pending,
    };
  }

  // COMPLETED from here down: the only terminal state.
  const daysSinceSold = getDaysSinceSold(getSoldDate(product), now);

  // Age ceiling on the sold page, because `gone` alone never gets there.
  //
  // `gone` is set by the nightly `set_products_gone` task, which keys on
  // `auction.end_date`. A COMPLETED auction with no `end_date` can
  // therefore never be marked gone, and the branch above never fires.
  // That is not an edge case: sampled against the production gateway on
  // 2026-08-12, all 154 COMPLETED products in a 600-URL sample of
  // `pdp_en_0.xml` had `end_date: null`, dated only by the `updated`
  // fallback in `getSoldDate`. Without this line every one of them
  // answers 200 forever and never reaches the archival redirect.
  //
  // So the 365-day boundary that `resolveSoldPhase` used stays in force
  // here as the second route into Archived: whichever of `gone` or age
  // arrives first ends the sold page.
  if (daysSinceSold !== null && daysSinceSold >= RECENTLY_GONE_MAX_AGE_DAYS) {
    return {
      availability: AVAILABILITY.Archived,
      status: 301,
      robots: null,
      render: "none",
      variant: null,
    };
  }

  /*
    Both remaining phases answer the same way, and the distinction is kept
    only so the age of a sold page stays legible in the props and in
    analytics.

    An undateable COMPLETED sale lands here too, as SoftSold. It should
    not happen, because `complete()` stamps `end_date`, but if it does,
    keeping the URL answering is recoverable and redirecting it away is
    not.
  */
  const availability =
    daysSinceSold !== null && daysSinceSold >= SOFT_SOLD_MAX_AGE_DAYS
      ? AVAILABILITY.RecentlyGone
      : AVAILABILITY.SoftSold;

  return {
    availability,
    status: 200,
    /*
      Stated positively rather than left null. Null would also be
      indexable, since the page carries no other robots tag, but silence
      reads as an oversight on a page that spent its whole life carrying
      `noindex`. `follow` was always right and is unchanged: this page
      exists partly to pass authority to the alternatives it links to.
    */
    robots: "index,follow",
    render: "unavailable",
    variant: UNAVAILABLE_VARIANT.Sold,
  };
};

/**
 * Strip a listing-page path down to the shape the gateway returns:
 * no leading or trailing slash, no whitespace.
 *
 * The gateway's `@translated` directive resolves a missing translation to
 * `""` rather than null, and an absent field interpolated into a template
 * literal becomes the literal string `"undefined"`. Both produced the bug
 * this function exists to stop: `/` and `/<locale>/undefined`.
 */
const normaliseListingPath = (value?: string | null) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim().replace(/^\/+/, "").replace(/\/+$/, "");

  if (!trimmed || trimmed === "undefined" || trimmed === "null") {
    return null;
  }

  return trimmed;
};

/**
 * Build the Long Gone redirect target from a preference-ordered list of
 * candidate listing-page paths.
 *
 * Guaranteed to return a locale-correct, non-empty path. It can never
 * return `/`, which is the whole point: a 301 to the homepage tells
 * Google and every assistant that the URL had no topical successor.
 */
export const buildFallbackListingUrl = (
  candidates: Array<string | null | undefined>,
  locale?: Locale,
  pageDefaultLocale: Locale = defaultLocale
) => {
  const resolvedLocale =
    locale && locales.includes(locale) ? locale : defaultLocale;

  const path =
    candidates.map(normaliseListingPath).find(Boolean) ??
    DEFAULT_FALLBACK_LISTING_PATH[toContentLocale(resolvedLocale)] ??
    LAST_RESORT_LISTING_PATH;

  return resolvedLocale === pageDefaultLocale
    ? `/${path}`
    : `/${resolvedLocale}/${path}`;
};
